import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { PageHeader, PageBody } from "@/components/app/page-header";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ResponsiveModal } from "@/components/ios/responsive-modal";
import { Calendar as CalendarIcon, Plus, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { PriorityBadge } from "@/components/app/ui-bits";
import { useServerFn } from "@tanstack/react-start";
import { logActivity } from "@/lib/activity.functions";
import { useLocaleFmt } from "@/lib/i18n/format";

export const Route = createFileRoute("/_authenticated/app/calendar")({
  component: CalendarPage,
});

type Event = {
  id: string;
  title: string;
  starts_at: string;
  ends_at: string | null;
  type: string | null;
  location: string | null;
  description: string | null;
  priority: string | null;
};

const TYPE_KEYS = ["hearing", "deadline", "appointment", "reminder"] as const;
const PRIORITY_KEYS = ["low", "medium", "high", "urgent"] as const;

function CalendarPage() {
  const { t } = useTranslation();
  const { localeTag } = useLocaleFmt();
  const [events, setEvents] = useState<Event[] | null>(null);
  const [open, setOpen] = useState(false);
  const logFn = useServerFn(logActivity);
  const [form, setForm] = useState({
    title: "",
    starts_at: "",
    ends_at: "",
    type: "appointment",
    location: "",
    description: "",
    priority: "medium",
  });

  const load = async () => {
    const { data, error } = await supabase
      .from("calendar_events")
      .select("*")
      .gte("starts_at", new Date(Date.now() - 86400000).toISOString())
      .order("starts_at");
    if (error) toast.error(error.message);
    setEvents((data ?? []) as Event[]);
  };

  useEffect(() => {
    load();
  }, []);

  const create = async () => {
    if (!form.title.trim()) return toast.error(t("pages.calendar.titleRequired"));
    if (!form.starts_at) return toast.error(t("pages.calendar.startRequired"));
    const { data: u } = await supabase.auth.getUser();
    if (!u.user) return;
    const { error } = await supabase.from("calendar_events").insert({
      owner_id: u.user.id,
      title: form.title,
      starts_at: new Date(form.starts_at).toISOString(),
      ends_at: form.ends_at ? new Date(form.ends_at).toISOString() : null,
      type: form.type,
      location: form.location || null,
      description: form.description || null,
      priority: form.priority,
    });
    if (error) return toast.error(error.message);
    await logFn({ data: { action: "calendar.create", entity: "calendar_event", metadata: { title: form.title, priority: form.priority } } }).catch(() => {});
    toast.success(t("pages.calendar.created"));
    setForm({ title: "", starts_at: "", ends_at: "", type: "appointment", location: "", description: "", priority: "medium" });
    setOpen(false);
    load();
  };

  const remove = async (id: string) => {
    if (!confirm(t("pages.calendar.deleteConfirm"))) return;
    const { error } = await supabase.from("calendar_events").delete().eq("id", id);
    if (error) return toast.error(error.message);
    await logFn({ data: { action: "calendar.delete", entity: "calendar_event", entity_id: id } }).catch(() => {});
    toast.success(t("pages.calendar.deleted"));
    load();
  };

  const typeLabel = (val: string | null) => {
    if (!val) return null;
    const map: Record<string, string> = {
      Udienza: "hearing", Scadenza: "deadline", Appuntamento: "appointment", Promemoria: "reminder",
      hearing: "hearing", deadline: "deadline", appointment: "appointment", reminder: "reminder",
    };
    const key = map[val];
    return key ? t(`pages.calendar.types.${key}`) : val;
  };

  return (
    <>
      <PageHeader
        title={t("pages.calendar.title")}
        description={t("pages.calendar.description")}
        actions={
          <Button className="gap-2 ios-tap" onClick={() => setOpen(true)}>
            <Plus className="h-4 w-4" /> {t("pages.calendar.newEvent")}
          </Button>
        }
      />
      <PageBody>
        {events === null ? (
          <Card className="flex items-center justify-center p-12">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </Card>
        ) : events.length === 0 ? (
          <Card className="flex flex-col items-center justify-center p-16 text-center">
            <CalendarIcon className="mb-4 h-10 w-10 text-muted-foreground" />
            <h3 className="font-display text-xl font-semibold">{t("pages.calendar.emptyTitle")}</h3>
            <p className="mt-1 text-sm text-muted-foreground">{t("pages.calendar.emptyDesc")}</p>
            <Button className="mt-4 gap-2" onClick={() => setOpen(true)}>
              <Plus className="h-4 w-4" /> {t("pages.calendar.createFirst")}
            </Button>
          </Card>
        ) : (
          <div className="space-y-3">
            {events.map((e) => (
              <Card key={e.id} className="group flex items-center gap-4 p-4">
                <div className="flex h-12 w-12 flex-col items-center justify-center rounded-lg bg-primary text-primary-foreground">
                  <span className="text-[10px] font-medium uppercase">
                    {new Date(e.starts_at).toLocaleDateString(localeTag, { month: "short" })}
                  </span>
                  <span className="font-display text-lg font-bold leading-none">
                    {new Date(e.starts_at).getDate()}
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold truncate">{e.title}</div>
                  <div className="text-sm text-muted-foreground truncate">
                    {new Date(e.starts_at).toLocaleString(localeTag, {
                      weekday: "short",
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                    {e.location && ` · ${e.location}`}
                  </div>
                </div>
                <div className="hidden sm:flex items-center gap-2">
                  {e.type && (
                    <span className="rounded-full bg-accent-muted px-3 py-1 text-xs font-medium text-accent-foreground">
                      {typeLabel(e.type)}
                    </span>
                  )}
                  <PriorityBadge priority={e.priority ?? "medium"} />
                </div>
                <Button
                  size="sm"
                  variant="ghost"
                  className="opacity-0 transition-opacity group-hover:opacity-100"
                  onClick={() => remove(e.id)}
                  aria-label={t("pages.calendar.ariaDelete")}
                >
                  <Trash2 className="h-4 w-4 text-destructive" />
                </Button>
              </Card>
            ))}
          </div>
        )}
      </PageBody>

      <ResponsiveModal
        open={open}
        onOpenChange={setOpen}
        title={t("pages.calendar.modalNew")}
        footer={<Button onClick={create} className="ios-tap">{t("pages.calendar.createEvent")}</Button>}
      >
        <div className="space-y-3">
          <div>
            <Label>{t("pages.calendar.fTitle")}</Label>
            <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>{t("pages.calendar.fStart")}</Label>
              <Input
                type="datetime-local"
                value={form.starts_at}
                onChange={(e) => setForm({ ...form, starts_at: e.target.value })}
              />
            </div>
            <div>
              <Label>{t("pages.calendar.fEnd")}</Label>
              <Input
                type="datetime-local"
                value={form.ends_at}
                onChange={(e) => setForm({ ...form, ends_at: e.target.value })}
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>{t("pages.calendar.fType")}</Label>
              <select
                value={form.type}
                onChange={(e) => setForm({ ...form, type: e.target.value })}
                className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm"
              >
                {TYPE_KEYS.map((k) => (
                  <option key={k} value={k}>{t(`pages.calendar.types.${k}`)}</option>
                ))}
              </select>
            </div>
            <div>
              <Label>{t("pages.calendar.fPriority")}</Label>
              <select
                value={form.priority}
                onChange={(e) => setForm({ ...form, priority: e.target.value })}
                className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm"
              >
                {PRIORITY_KEYS.map((p) => (
                  <option key={p} value={p}>{t(`pages.calendar.priorities.${p}`)}</option>
                ))}
              </select>
            </div>
          </div>
          <div>
            <Label>{t("pages.calendar.fLocation")}</Label>
            <Input
              value={form.location}
              onChange={(e) => setForm({ ...form, location: e.target.value })}
              placeholder={t("pages.calendar.fLocationPh")}
            />
          </div>
          <div>
            <Label>{t("pages.calendar.fNotes")}</Label>
            <Textarea
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              rows={3}
            />
          </div>
        </div>
      </ResponsiveModal>
    </>
  );
}
